/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import { graphql, useStaticQuery } from "gatsby"
import he from "he"
import PropTypes from "prop-types"
import React from "react"
import { Helmet } from "react-helmet"

const SEO = ({
  title,
  uri,
  lang,
  headJson,
  translations,
  preLoadImage,
  preLoadImageWidths,
  preLoadImageAspectRatio,
}) => {
  const titleDecoded = title ? he.decode(title) : title

  let head = { link: [] }

  if (headJson) {
    head = JSON.parse(headJson)
  }

  const data = useStaticQuery(
    graphql`
      {
        site {
          siteMetadata {
            siteUrl
            # facebookDomainVerification
          }
        }
      }
    `
  )

  // Preload the main image above the fold
  // Assumes the image takes 100vw
  // This has a lot in common with the @atoms/Image component

  if (
    preLoadImage &&
    preLoadImage.cloudinaryDetails &&
    preLoadImageWidths &&
    process.env.GATSBY_IMAGE_PROXY_URL
  ) {
    const cloudinaryAspectRatio = preLoadImageAspectRatio
      ? ",ar_" + preLoadImageAspectRatio + ",c_fill/"
      : ""

    const sourcePath = `${preLoadImage.cloudinaryDetails.public_id}/${preLoadImage.cloudinaryDetails.original_filename}.${preLoadImage.cloudinaryDetails.format}`

    const preLoadImageUrl =
      process.env.GATSBY_IMAGE_PROXY_URL +
      "/f_auto,q_auto" +
      cloudinaryAspectRatio +
      "/" +
      sourcePath

    const preLoadSrcSetStringArray = preLoadImageWidths.map((size) => {
      return `${process.env.GATSBY_IMAGE_PROXY_URL}/w_${size},f_auto,q_auto${cloudinaryAspectRatio}/${sourcePath} ${size}w`
    })

    const preLoadSrcSetString = preLoadSrcSetStringArray.join(", ")

    head.link.push({
      rel: "preload",
      as: "image",
      fetchpriority: "high",
      href: preLoadImageUrl,
      imagesrcset: preLoadSrcSetString,
      imagesizes: "100vw",
    })
  }

  // DNS prefetch 3rd party domains

  head.link.push({ rel: "dns-prefetch", href: "https://snap.licdn.com" })
  head.link.push({ rel: "dns-prefetch", href: "https://www.facebook.com" })
  head.link.push({ rel: "dns-prefetch", href: "https://script.hotjar.com" })
  head.link.push({ rel: "dns-prefetch", href: "https://static.hotjar.com" })
  head.link.push({
    rel: "dns-prefetch",
    href: "https://static.ads-twitter.com",
  })

  // Add facebook domain verification meta tag
  const fbDomainVerificationCode =
    data?.site?.siteMetadata?.facebookDomainVerification || ""
  const fbTag = {
    name: "facebook-domain-verification",
    content: fbDomainVerificationCode,
  }
  if ("meta" in head) {
    if (Array.isArray(head.meta)) {
      head.meta.push(fbTag)
    }
  } else {
    head.meta = [fbTag]
  }

  const siteUrl = data?.site?.siteMetadata?.siteUrl || ""

  const meta = head?.meta
    ? head.meta.map((meta) => {
        meta.content =
          typeof meta.content === "string" && meta.content
            ? he.decode(meta.content)
            : meta.content
        return meta
      })
    : []

  const normalizeLocale = (locale) => {
    return locale.toLowerCase().replace("_", "-")
  }

  let hreflang = translations
    ? translations.map((translation) => {
        return {
          rel: "alternate",
          hreflang: normalizeLocale(translation?.language?.locale || ""),
          href: siteUrl + translation?.uri,
        }
      })
    : []

  if (lang && uri) {
    hreflang.unshift({
      rel: "alternate",
      hreflang: normalizeLocale(lang || ""),
      href: siteUrl + uri,
    })
  }

  let defaultFound = false

  hreflang.forEach((item) => {
    if (item.hreflang === "en") {
      hreflang.push({
        rel: "alternate",
        hreflang: "x-default",
        href: item.href,
      })
      defaultFound = true
    }
  })

  if (!defaultFound) {
    // No translation found in default language (en)
    // use the current language
    hreflang.forEach((item) => {
      if (item.hreflang === lang) {
        hreflang.push({
          rel: "alternate",
          hreflang: "x-default",
          href: item.href,
        })
        defaultFound = true
      }
    })
  }

  hreflang = hreflang.filter((item) => {
    return item.hreflang.length > 0
  })

  const link = [].concat(hreflang, head?.link || [])

  const structuredData = head?.structuredData

  return (
    <Helmet title={titleDecoded} meta={meta} link={link}>
      {structuredData && (
        <script type="application/ld+json">{`${JSON.stringify(
          structuredData
        )}`}</script>
      )}
    </Helmet>
  )
}

SEO.defaultProps = {
  title: "",
  lang: `en`,
  headJson: "",
}

SEO.propTypes = {
  title: PropTypes.string.isRequired,
  lang: PropTypes.string,
  headJson: PropTypes.string,
}

export default SEO
